import { AriaOrb } from "../AriaOrb";
import ConfidenceChip from "../ConfidenceChip";
import FeedbackButtons from "../FeedbackButtons";
import { DrillSuggestion } from "../DrillSuggestion";
import { AgentTrace } from "../AgentTrace";
import { FileText, Volume2, Square, Loader2 } from "lucide-react";
import { SaveHandoff } from "../projects/SaveHandoff";
import { useSpeech } from "../../hooks/useSpeech";
import { AttachmentList } from "../attachments/AttachmentList";

const SUGGESTIONS = [
  "What's the relationship between entropy and the second law?",
  "What am I weakest at right now?",
  "Explain Gibbs free energy and protein folding.",
  "Prepare me for Friday's interview.",
];

function TypingDots() {
  return <span className="inline-flex items-center gap-1"><i className="typing-dot" /><i className="typing-dot" /><i className="typing-dot" /></span>;
}

export function ChatWelcome({ accent, welcome, onPick }) {
  return (
    <div className="text-center py-10 fade-in">
      <AriaOrb size={80} color={accent} className="mx-auto mb-6" />
      <div className="aria-pill aria-pill-primary mb-3 inline-flex">Academic Intelligence</div>
      <h2 className="font-display text-4xl tracking-tight mb-3 font-medium">How can I help today?</h2>
      <p className="text-sm text-[var(--aria-text-muted)] mb-8 max-w-md mx-auto leading-relaxed">
        {welcome || "I've connected your lectures, notes, and research. Try one of these, or ask anything in your own words."}
      </p>
      <div className="grid sm:grid-cols-2 gap-2 max-w-xl mx-auto fade-in-stagger">
        {SUGGESTIONS.map(q => (
          <button key={q} onClick={() => onPick(q)} data-testid={`suggestion-${q.slice(0, 20)}`}
            className="btn text-left p-3.5 rounded-md border border-[var(--aria-border)] hover:border-[var(--aria-primary)] hover:bg-[var(--aria-primary-tint)] text-sm transition-all">
            <span className="text-[var(--aria-primary)] mr-1.5 font-mono text-[10px]">▸</span>{q}
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ m, i, isLast, streaming, accent, activeId, feedbackIndex, onCite, voice }) {
  const isUser = m.role === "user";
  const voiceKey = `msg-${i}`;
  const isSpeaking = voice.speaking && voice.activeKey === voiceKey;
  const isLoading = voice.loadingKey === voiceKey;
  return (
    <div className={`flex gap-3 fade-in ${isUser ? "justify-end" : ""}`}>
      {!isUser && <AriaOrb size={28} color={accent} state={isLast && streaming ? "thinking" : "idle"} />}
      <div className={`min-w-0 max-w-[94%] sm:max-w-[78%] rounded-2xl p-3 sm:p-4 ${isUser
        ? "bg-[var(--aria-text)] text-[var(--aria-bg)] rounded-tr-sm"
        : "bg-[var(--aria-surface)] border border-[var(--aria-border)] rounded-tl-sm"}`} data-testid={`message-${m.role}-${i}`}>
        {!isUser && <AgentTrace steps={m.steps} streaming={!!m._streaming} />}
        {m._streaming && !m.content ? (
          <div className="py-1"><TypingDots /></div>
        ) : (
          <div className="whitespace-pre-wrap break-words text-sm leading-relaxed">
            {m.content}
            {m._streaming && <span className="stream-cursor" aria-hidden />}
          </div>
        )}
        <AttachmentList files={m.attachments} prefix={`aria-message-${i}`} />
        {!isUser && !m._streaming && m.content && (
          <button
            onClick={() => (isSpeaking ? voice.stop() : voice.speak(m.content, voiceKey))}
            data-testid={`message-listen-${i}`}
            title={isSpeaking ? "Stop" : "Listen"}
            className={`mt-2.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md border text-xs transition-colors ${isSpeaking ? "border-[var(--aria-primary)] text-[var(--aria-primary)] bg-[var(--aria-primary)]/10" : "border-[var(--aria-border)] text-[var(--aria-text-muted)] hover:text-[var(--aria-text)] hover:border-[var(--aria-primary)]/60"}`}
          >
            {isLoading ? <Loader2 size={12} className="animate-spin" /> : isSpeaking ? <Square size={12} /> : <Volume2 size={12} />}
            {isSpeaking ? "Stop" : "Listen"}
          </button>
        )}
        {m.sources?.length > 0 && (
          <details className="mt-3 pt-2 border-t border-current/10" open data-testid={`sources-used-${i}`}>
            <summary className="text-xs cursor-pointer mb-2" data-testid={`sources-used-toggle-${i}`}>Sources used</summary>
            <div className="flex flex-wrap gap-1.5">
            {m.sources.map((s, j) => (
              <button key={`${s}-${j}`} data-testid={`citation-pill-${i}-${j}`} onClick={() => onCite(s, m.citations || [])}
                className="aria-pill aria-pill-accent citation-pill" title={`View excerpts from ${s}`}>
                <FileText size={10} /> {s}
              </button>
            ))}
            </div>
          </details>
        )}
        {!isUser && !m._streaming && <DrillSuggestion text={m.content} messageIndex={i} />}
        {!isUser && !m._streaming && m.confidence && activeId && <SaveHandoff conversationId={activeId} messageIndex={feedbackIndex} />}
        {!isUser && !m._streaming && m.confidence && (
          <div className="mt-3 pt-2 border-t border-current/10 flex items-center justify-between gap-2 flex-wrap">
            <ConfidenceChip confidence={m.confidence} testId={`confidence-chip-${i}`} />
            {activeId && <FeedbackButtons conversationId={activeId} messageIndex={feedbackIndex} initialValue={m.feedback} />}
          </div>
        )}
      </div>
    </div>
  );
}

export function MessageList({ messages, streaming, accent, activeId, onCite }) {
  const voice = useSpeech();
  // Backend persists only user↔assistant turns; the client-only welcome greeting
  // (assistant at index 0 with no confidence, not streaming) offsets indices by 1.
  const offset = messages[0]?.role === "assistant" && !messages[0]?.confidence && !messages[0]?._streaming ? 1 : 0;
  return messages.map((m, i) => (
    <MessageBubble key={m.timestamp ? `${m.role}-${m.timestamp}` : `msg-${i}`} m={m} i={i}
      isLast={i === messages.length - 1} streaming={streaming} accent={accent} activeId={activeId}
      feedbackIndex={i - offset} onCite={onCite} voice={voice} />
  ));
}
